import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { Menu, X, Phone, Mail, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSiteSettings } from "@/lib/cms";
import { fileUrl } from "@/lib/format";

const NAV = [
  { href: "/", key: "home" },
  { href: "/rolunk", key: "about" },
  { href: "/projektek", key: "projects" },
  { href: "/kapcsolat", key: "contact" },
] as const;

export function Header() {
  const { t } = useTranslation();
  const settings = useSiteSettings();
  const { brand } = settings;
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [location] = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (href: string) =>
    href === "/" ? location === "/" : location.startsWith(href);

  return (
    <>
      {/* ─── Persistent left vertical sidebar ─────────────────────── */}
      <aside className="hidden lg:flex fixed left-0 top-0 bottom-0 w-12 bg-white border-r border-brand-100 z-40 flex-col items-center justify-between py-6">
        {/* Logo on top */}
        <Link
          href="/"
          aria-label={brand.shortName}
          className="block group"
        >
          {brand.logoImageKey ? (
            <img
              src={fileUrl(brand.logoImageKey)}
              alt={brand.shortName}
              className="w-8 h-8 object-contain"
            />
          ) : (
            <div className="w-8 h-8 grid place-items-center font-heading font-bold text-xl text-brand-900 border-2 border-accent group-hover:bg-accent group-hover:text-white transition-colors">
              {brand.logoLetter}
            </div>
          )}
        </Link>

        {/* Vertical "Lépjen kapcsolatba velünk" text */}
        <div
          className="text-[10px] font-semibold uppercase tracking-widish text-brand-500"
          style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
        >
          Lépjen kapcsolatba velünk
        </div>

        {/* Social icons bottom */}
        <div className="flex flex-col gap-4">
          <a
            href={`tel:${brand.phone.replace(/\s/g, "")}`}
            className="text-brand-400 hover:text-accent transition-colors"
            aria-label="Telefon"
          >
            <Phone size={15} />
          </a>
          <a
            href={`mailto:${brand.email}`}
            className="text-brand-400 hover:text-accent transition-colors"
            aria-label="Email"
          >
            <Mail size={15} />
          </a>
          <Link
            href="/kapcsolat"
            className="text-brand-400 hover:text-accent transition-colors"
            aria-label="Kapcsolat"
          >
            <MapPin size={15} />
          </Link>
        </div>
      </aside>

      {/* ─── Sticky top nav (appears on scroll) ───────────────────── */}
      <header
        className={`fixed top-0 left-0 right-0 lg:left-12 z-30 transition-all duration-500 ${
          scrolled
            ? "bg-white/95 backdrop-blur shadow-[0_2px_12px_rgba(0,0,0,0.06)] translate-y-0"
            : "bg-transparent -translate-y-full lg:translate-y-0 lg:bg-transparent"
        }`}
      >
        <div className="container flex items-center justify-between h-16 lg:h-20">
          {/* Mobile-only inline logo (because the vertical sidebar is hidden on small screens) */}
          <Link
            href="/"
            className={`flex items-center gap-3 group lg:opacity-0 lg:pointer-events-none transition-opacity ${
              scrolled ? "lg:opacity-100 lg:pointer-events-auto" : ""
            }`}
          >
            {brand.logoImageKey ? (
              <img
                src={fileUrl(brand.logoImageKey)}
                alt={brand.shortName}
                className="h-8 w-auto"
              />
            ) : (
              <div className="w-9 h-9 bg-accent text-white grid place-items-center font-heading font-bold text-lg">
                {brand.logoLetter}
              </div>
            )}
            <span className="font-heading uppercase tracking-widish text-xs sm:text-sm text-brand-700 group-hover:text-accent transition-colors">
              {brand.shortName}
            </span>
          </Link>

          {/* Right-aligned nav */}
          <nav className="hidden md:flex items-center gap-1 ml-auto">
            {NAV.map((item) => {
              const active = isActive(item.href);
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`px-4 py-2 text-xs font-semibold uppercase tracking-widish transition-colors ${
                    active
                      ? "text-accent"
                      : scrolled || location !== "/"
                        ? "text-brand-700 hover:text-accent"
                        : "text-white hover:text-accent drop-shadow"
                  }`}
                >
                  {t(`nav.${item.key}`)}
                </Link>
              );
            })}
          </nav>

          <button
            type="button"
            className={`md:hidden p-2 ${
              scrolled || location !== "/" ? "text-brand-700" : "text-white"
            } hover:text-accent`}
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <div className="md:hidden border-t border-brand-100 bg-white">
            <nav className="container py-3 flex flex-col gap-0.5">
              {NAV.map((item) => {
                const active = isActive(item.href);
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={() => setOpen(false)}
                    className={`px-3 py-3 text-xs font-semibold uppercase tracking-widish ${
                      active
                        ? "text-accent"
                        : "text-brand-700 hover:text-accent"
                    }`}
                  >
                    {t(`nav.${item.key}`)}
                  </Link>
                );
              })}
            </nav>
          </div>
        )}
      </header>
    </>
  );
}
